/**
 * Mirrors BC_Ticketing's `Models/Enums/TicketingEnums.cs`. The API serialises these as numbers, so
 * the values here must match it exactly — never reorder or renumber.
 */

export enum TicketType {
  Bug = 0,
  Feature = 1,
}

/**
 * The developer workflow. Reporters never see these — they see {@link ReporterTicketStatus}, which
 * the API returns alongside on every ticket.
 */
export enum TicketStatus {
  New = 0,
  Triaged = 1,
  InProgress = 2,
  InReview = 3,
  Resolved = 4,
  NeedsInfo = 5,
  WontDo = 6,
}

/**
 * What a reporter is shown — coarser than {@link TicketStatus} on purpose.
 *
 * **Never derive this from the dev status on the client.** The API returns both on every ticket, and
 * the mapping lives server-side in `TicketStatusMapper`. Two copies of that table drift, and the way
 * you find out is a customer told their bug is "In review" on one screen and "Submitted" on another.
 */
export enum ReporterTicketStatus {
  Submitted = 0,
  InProgress = 1,
  NeedsYourInput = 2,
  Resolved = 3,
  WontDo = 4,
}

/** Set by a developer at triage, and the input to the to-do due date. */
export enum TicketSeverity {
  S1 = 1,
  S2 = 2,
  S3 = 3,
  S4 = 4,
}

/** Impact as the reporter perceives it — deliberately separate from severity. */
export enum ReporterPriority {
  Low = 0,
  Normal = 1,
  BlockingMyWork = 2,
}

export enum TicketAttachmentKind {
  Screenshot = 0,
  File = 1,
}

export enum TicketEventType {
  Created = 0,
  StatusChanged = 1,
  Assigned = 2,
  SeverityChanged = 3,
  ModuleChanged = 4,
  MarkedDuplicate = 5,
  Commented = 6,
  AttachmentAdded = 7,
  Closed = 8,
  Reopened = 9,
  TodoCreated = 10,
  DevOpsLinked = 11,
  DevOpsHint = 12,
}

/** What a list request asks to cover. The API narrows it to what the caller may actually have. */
export enum TicketScope {
  Mine = 0,
  Company = 1,
  All = 2,
}

/** The labels shown to a reporter. Plain words, per the design's content rules. */
export const REPORTER_STATUS_LABEL: Record<ReporterTicketStatus, string> = {
  [ReporterTicketStatus.Submitted]: 'Submitted',
  [ReporterTicketStatus.InProgress]: 'In progress',
  [ReporterTicketStatus.NeedsYourInput]: 'Needs your input',
  [ReporterTicketStatus.Resolved]: 'Resolved',
  [ReporterTicketStatus.WontDo]: "Won't do",
};

/** The fuller workflow, for the Dev board and the developer drawer. */
export const DEV_STATUS_LABEL: Record<TicketStatus, string> = {
  [TicketStatus.New]: 'New',
  [TicketStatus.Triaged]: 'Triaged',
  [TicketStatus.InProgress]: 'In progress',
  [TicketStatus.InReview]: 'In review',
  [TicketStatus.Resolved]: 'Resolved',
  [TicketStatus.NeedsInfo]: 'Needs info',
  [TicketStatus.WontDo]: "Won't do",
};

export const TICKET_TYPE_LABEL: Record<TicketType, string> = {
  [TicketType.Bug]: 'Bug',
  [TicketType.Feature]: 'Feature',
};

export const REPORTER_PRIORITY_LABEL: Record<ReporterPriority, string> = {
  [ReporterPriority.Low]: 'Low',
  [ReporterPriority.Normal]: 'Normal',
  [ReporterPriority.BlockingMyWork]: 'Blocking my work',
};

/**
 * The board's columns, in order. Resolved and Won't do are filters rather than columns — a board that
 * grows a "done" column forever stops being a working surface.
 */
export const BOARD_COLUMNS: readonly TicketStatus[] = [
  TicketStatus.New,
  TicketStatus.Triaged,
  TicketStatus.InProgress,
  TicketStatus.InReview,
  TicketStatus.NeedsInfo,
] as const;
